import { Box, Card, Heading } from '@chakra-ui/react'
import React from 'react'
import { Link } from 'react-router'

import { ExtensionsApiProvider } from '../api/context'
import type { ExtensionsApi } from '../api/extensionsApi'
import { useGetExtensionInfo } from '../hooks'
import { ExtensionsI18nProvider, useExtensionsI18n } from '../i18n/provider'
import type { ExtensionsTranslationLike } from '../i18n/types'
import {
  extensionBelongsToPbx,
  getEditSlugForExtensionType,
} from '../utils/extensions'
import {
  CallQueueEditPage,
  ConferenceRoomEditPage,
  type ExtensionEditPageContracts,
  LineEditPage,
  RingGroupEditPage,
  SharedVoicemailEditPage,
  VirtualFaxEditPage,
} from './edit'
import { renderWithWrapper } from './edit/renderWithWrapper'
import { ExtensionEditPlaceholderPage } from './ExtensionEditPlaceholderPage'
import { getListLabelForSlug, getTypeLabelForSlug } from './typeSlugLabels'

export interface ExtensionEditByTypePageProps extends ExtensionEditPageContracts {
  api: ExtensionsApi
  pbxId: number
  type: string
  extensionId: string
  fallback?: React.ComponentType<{ type: string }>
  /** i18next translation function. Pass `t` from `useTranslation()` to enable i18n. */
  t?: ExtensionsTranslationLike
  /** Current locale code (e.g. 'en-US', 'es'). */
  locale?: string
}

const ExtensionEditByTypeInner: React.FC<
  Omit<ExtensionEditByTypePageProps, 'api'>
> = ({ type, ...props }) => {
  const { text } = useExtensionsI18n()
  const isAddMode = props.extensionId === 'new'
  const extensionIdNum = !isAddMode
    ? parseInt(props.extensionId, 10)
    : undefined
  const isValidId =
    !isAddMode && extensionIdNum != null && !Number.isNaN(extensionIdNum)

  // Do not gate rendering on isLoading: the type editors withhold the form
  // themselves, and gating here serializes their other queries behind this one.
  const { data: extension, isError } = useGetExtensionInfo({
    extensionId: extensionIdNum ?? 0,
    options: { enabled: isValidId },
  })

  const listPath = props.getListRoute({ pbxId: props.pbxId, type })
  const listLabel = getListLabelForSlug(type, text)

  const renderNotice = (heading: string, body?: React.ReactNode) => {
    const typeLabel = getTypeLabelForSlug(type, text)
    const breadcrumbs = [
      { label: listLabel, path: listPath },
      {
        label: isAddMode
          ? text('extensions.edit.breadcrumbAdd', 'Add {{typeLabel}}', {
              typeLabel,
            })
          : text(
              'extensions.edit.breadcrumbEdit',
              'Edit {{typeLabel}} {{value}}',
              { typeLabel, value: props.extensionId },
            ),
      },
    ]

    return renderWithWrapper(
      props.wrapper,
      breadcrumbs,
      <Card.Root>
        <Card.Body p={6}>
          <Heading size="md" mb={2}>
            {heading}
          </Heading>
          {body}
          <Link
            to={listPath}
            style={{ color: 'var(--chakra-colors-blue-500)' }}
          >
            {text('extensions.edit.backToList', '← Back to {{listLabel}}', {
              listLabel,
            })}
          </Link>
        </Card.Body>
      </Card.Root>,
    )
  }

  // Type editors render a spinner forever on a failed fetch, so stop here
  // rather than dispatching into one. `isError` stays set when a refetch fails
  // over cached data, so require no data too — swapping out then unmounts the
  // editor and loses unsaved edits.
  if (isValidId && isError && !extension) {
    return renderNotice(
      text('extensions.edit.loadFailed', 'This extension could not be loaded'),
    )
  }

  if (
    isValidId &&
    extension &&
    // Fail open on a payload with no pbx rather than blocking every editor.
    extension.pbx?.id != null &&
    !extensionBelongsToPbx(extension, props.pbxId)
  ) {
    const correctPbxId = extension.pbx.id
    // The URL slug may name a different type than the extension really is.
    const targetType = getEditSlugForExtensionType(extension.type)
    const editRouteFor = (pbxId: number) =>
      targetType
        ? props.getEditRoute({
            pbxId,
            type: targetType,
            extensionId: props.extensionId,
          })
        : null
    // Suppress the link for hosts whose edit routes omit pbxId — it would
    // point straight back here.
    const correctPath = editRouteFor(correctPbxId)
    const openInPbxPath =
      correctPath === editRouteFor(props.pbxId) ? null : correctPath

    return renderNotice(
      text(
        'extensions.edit.wrongPbx',
        'This extension is not in the selected PBX',
      ),
      <>
        <Box mb={4}>
          {text(
            'extensions.edit.wrongPbxDescription',
            'This extension does not belong to the selected PBX.',
          )}
        </Box>
        {openInPbxPath ? (
          <Box mb={4}>
            <Link
              to={openInPbxPath}
              style={{ color: 'var(--chakra-colors-blue-500)' }}
            >
              {text('extensions.edit.openInPbx', 'Open in {{pbxName}}', {
                pbxName: extension.pbx?.name ?? String(correctPbxId),
              })}
            </Link>
          </Box>
        ) : null}
      </>,
    )
  }

  if (type === 'line') return <LineEditPage type={type} {...props} />
  if (type === 'ring-group') return <RingGroupEditPage type={type} {...props} />
  if (type === 'call-queue') return <CallQueueEditPage type={type} {...props} />
  if (type === 'conference-room')
    return <ConferenceRoomEditPage type={type} {...props} />
  if (type === 'virtual-fax')
    return <VirtualFaxEditPage type={type} {...props} />
  if (type === 'shared-voicemail')
    return <SharedVoicemailEditPage type={type} {...props} />

  if (props.fallback) {
    const Fallback = props.fallback
    return <Fallback type={type} />
  }
  return (
    <ExtensionEditPlaceholderPage
      type={type}
      listRoute={listPath}
      wrapper={props.wrapper}
    />
  )
}

export const ExtensionEditByTypePage: React.FC<
  ExtensionEditByTypePageProps
> = ({ api, ...props }) => {
  return (
    <ExtensionsApiProvider api={api}>
      <ExtensionsI18nProvider t={props.t} locale={props.locale}>
        <ExtensionEditByTypeInner {...props} />
      </ExtensionsI18nProvider>
    </ExtensionsApiProvider>
  )
}
